import Hyperswarm from 'hyperswarm'
import Corestore from 'corestore'
import b4a from 'b4a'
import { watchConnection } from './liveness.js'

const RECONNECT_BASE = 1000
const RECONNECT_MAX = 30000

// How often to ask the platform whether the link it gave us is still up.
// Nothing below this layer is obliged to announce a death — the relay's DHT
// object, in particular, does not — so the answer is polled.
const LIVENESS_INTERVAL = 15000

// How long to wait for a dead swarm to admit it is dead before moving on.
const TEARDOWN_TIMEOUT = 4000

// A link that has carried nothing for a check in a row this many times is
// taken for dead. Two, not one: a phone coming out of sleep runs its timers
// before the messages that queued up while it slept, and one stale reading
// on its own is how every wake-up became a needless reconnect.
const STALE_STRIKES = 2

// After a phone wakes, how long to let queued traffic land before believing
// that the link is silent.
const SETTLE = 3000

// A ringing call wakes the phone every ten seconds; one renewal of its
// announcements is all it needs. See reannounce().
const REANNOUNCE_GAP = 20000

/**
 * One object that hides the difference between "this runtime speaks UDP" and
 * "this runtime is a browser tab".
 *
 * On Node, Bare and Electron we open a real Hyperswarm on the public DHT.
 * In a browser there is no UDP, so we tunnel the same DHT over a WebSocket to
 * a relay (`@hyperswarm/dht-relay`). Everything above this layer — Autobase,
 * Hyperbee, blobs, pairing — is byte-for-byte identical either way, and the
 * relay only ever sees Noise-encrypted frames it cannot read.
 */
export class Transport {
  constructor ({ storage, keyPair = null, createDHT, createStorage, name = 'pine', storeOptions = null } = {}) {
    this._createDHT = createDHT
    this._createStorage = createStorage
    this._storage = storage
    this._name = name
    this._keyPair = keyPair
    this._storeOptions = storeOptions

    this.store = null
    this.swarm = null
    this.dht = null
    this.mode = 'unknown'
    this.status = 'offline'

    this.opening = null
    this.closing = null
    this.destroyed = false

    this._topics = new Map() // hex -> { topic, server, client }
    this._listeners = new Set()
    this._connectionHandlers = new Set()
    this._reconnectAttempt = 0
    this._reconnectTimer = null

    // Bumped on every connect attempt. A link that dies after it has been
    // replaced must not tear down its successor, and on a flaky mobile
    // network that overlap is the normal case, not the rare one.
    this._generation = 0
    this._alive = null
    this._fresh = null
    this._livenessTimer = null
    this._reannouncedAt = 0
  }

  /* ---- lifecycle ---- */

  ready () {
    if (!this.opening) this.opening = this._open()
    return this.opening
  }

  /**
   * Opening must not depend on the network.
   *
   * Local storage is the part that has to work: rooms, history and the
   * identity all live on disk, and a messenger that refuses to open its own
   * archive because a relay is down is worse than useless. So the swarm is
   * brought up in the background and retried, and the UI just shows "offline"
   * until it lands.
   */
  async _open () {
    this.store = new Corestore(this._createStorage(this._storage, this._name), this._storeOptions || undefined)
    await this.store.ready()

    this._connect().catch(() => this._onTransportLost())
  }

  async _connect () {
    const generation = ++this._generation
    this._setStatus('connecting')

    // `alive` is how a platform reports a link that died without saying so.
    // The relay path needs it: @hyperswarm/dht-relay's node never emits
    // 'close', so a dropped WebSocket used to leave this object reporting
    // "online" with nothing whatsoever crossing it — the phone looked
    // connected and messages simply stopped, until the app was restarted.
    let made
    try {
      made = await this._createDHT({
        onLost: () => { if (generation === this._generation) this._onTransportLost() }
      })
    } catch (err) {
      // An attempt that something newer has already replaced fails quietly:
      // reporting it would schedule a reconnect over the top of the attempt
      // that is in flight right now, and throw that one away too.
      if (generation !== this._generation) return
      throw err
    }
    const { dht, mode, alive = null, fresh = null } = made

    if (generation !== this._generation || this.destroyed) {
      // Superseded while we were opening; drop it rather than leak it.
      try { await dht.destroy() } catch {}
      return
    }

    this.dht = dht
    this.mode = mode
    this._alive = alive
    this._fresh = fresh

    this.swarm = new Hyperswarm({
      dht,
      keyPair: this._keyPair || undefined,
      maxPeers: 64,
      maxParallel: mode === 'relay' ? 8 : 32
    })

    this.swarm.on('connection', (conn, info) => {
      conn.on('error', () => {})
      this.store.replicate(conn)
      // Every connection says it is alive; only a relayed one is closed for
      // going quiet. A native one learns about a dead peer from UDX already.
      // See liveness.js.
      watchConnection(conn, { judge: mode === 'relay' })
      for (const fn of this._connectionHandlers) fn(conn, info)
    })

    if (dht.on) {
      dht.on('close', () => { if (generation === this._generation) this._onTransportLost() })
      dht.on('error', () => {})
    }

    // Re-announce every topic we were on before the reconnect.
    for (const entry of this._topics.values()) {
      entry.discovery = this.swarm.join(entry.topic, {
        server: entry.server,
        client: entry.client
      })
    }

    this._reconnectAttempt = 0
    this._setStatus('online')
    this._watchLiveness()
  }

  /**
   * Poll the link, because not every kind of link announces its own death.
   *
   * Cheap — one predicate on a timer — and it is the only thing that catches
   * a WebSocket the runtime closed while the app was in the background, which
   * is the ordinary fate of the Android build every time the screen goes off.
   */
  _watchLiveness () {
    if (this._livenessTimer) clearInterval(this._livenessTimer)
    if (!this._alive) return
    /*
     * Two questions. Is the socket still open — which it says straight away
     * when it is closed. And has anything come over it lately — which is the
     * only way to find out about the other kind of dead: a phone that moved
     * from Wi-Fi to mobile data, or switched a VPN on, keeps the old socket
     * "open" on a network it is no longer on, and nothing ever arrives on it
     * again. The relay says something every fifteen seconds, so silence is
     * an answer.
     */
    let strikes = 0
    this._livenessTimer = setInterval(() => {
      if (this.destroyed || this.closing || this.status !== 'online') return
      if (!this._alive()) return this._onTransportLost()
      if (this._fresh && !this._fresh()) {
        if (++strikes >= STALE_STRIKES) this._onTransportLost({ immediate: true })
      } else {
        strikes = 0
      }
    }, LIVENESS_INTERVAL)
    if (this._livenessTimer.unref) this._livenessTimer.unref()
  }

  _onTransportLost ({ immediate = false } = {}) {
    if (this.destroyed || this.closing) return
    // Nothing that follows may be answered by the link that just died.
    this._generation++
    this._alive = null
    this._fresh = null
    if (this._livenessTimer) {
      clearInterval(this._livenessTimer)
      this._livenessTimer = null
    }
    this._setStatus('reconnecting')
    if (this._reconnectTimer) {
      if (!immediate) return
      clearTimeout(this._reconnectTimer)
      this._reconnectTimer = null
    }

    const delay = immediate
      ? 0
      : Math.min(RECONNECT_MAX, RECONNECT_BASE * Math.pow(2, this._reconnectAttempt++))

    this._reconnectTimer = setTimeout(async () => {
      this._reconnectTimer = null
      if (this.destroyed) return
      try {
        await this._teardownSwarm()
        await this._connect()
      } catch {
        this._onTransportLost()
      }
    }, delay)
  }

  /**
   * Drop the old swarm before building a new one.
   *
   * Forcefully, and with a deadline. A graceful destroy first unannounces
   * every topic, which means waiting on round trips over the very link that
   * just died — it does not time out, so the reconnect never ran and the
   * client stayed on "reconnecting" for as long as the app was open. There is
   * nothing to say goodbye to over a socket that is already gone.
   */
  async _teardownSwarm () {
    const swarm = this.swarm
    this.swarm = null
    if (!swarm) return
    // Destroying the swarm destroys the DHT node it was given as well — the
    // relay's WebSocket with it — so a reconnect leaves no old link behind.
    await Promise.race([
      swarm.destroy({ force: true }).catch(() => {}),
      new Promise((resolve) => setTimeout(resolve, TEARDOWN_TIMEOUT))
    ])
  }

  /**
   * The app came back to the foreground, or the device says it has a network
   * again. Check the link now instead of waiting out a backoff that may have
   * grown to half a minute while the screen was off.
   */
  wake () {
    if (this.destroyed || this.closing || !this.opening) return
    if (this.status === 'online') {
      if (this._alive && !this._alive()) return this._onTransportLost({ immediate: true })
      // Open — but open after a sleep says little. See check().
      this.check().catch(() => {})
      return
    }
    if (this.status === 'connecting') return
    this._reconnectAttempt = 0
    this._onTransportLost({ immediate: true })
  }

  /**
   * Is the link still carrying anything? Reconnects when it is not.
   *
   * For the moments something outside says "look now": the app coming back
   * to the screen, and the Android shell's nine-minute alarm, which wakes
   * a phone that would otherwise sleep through its link dying — a sleeping
   * phone runs no timers, so the liveness poll above never gets the chance.
   * The wait first lets whatever queued up during the sleep arrive.
   */
  async check () {
    if (this.destroyed || this.closing || !this.opening) return
    if (this.status !== 'online') return this.wake()
    if (!this._fresh || this._fresh()) return
    const generation = this._generation
    await new Promise((resolve) => setTimeout(resolve, SETTLE))
    if (generation !== this._generation || this.status !== 'online') return
    if (this._fresh && !this._fresh()) this._onTransportLost({ immediate: true })
  }

  /**
   * Start the link over, now, whatever it looks like.
   *
   * For when the platform knows better than the socket: the phone's network
   * changed underneath it. The old link went with the old network — a
   * Wi-Fi it has left, a VPN tunnel that is gone — and waiting for it to
   * admit that took minutes, during which nothing reached the phone. A fresh
   * link takes a second or two.
   */
  reconnect () {
    if (this.destroyed || this.closing || !this.opening) return
    this._reconnectAttempt = 0
    this._onTransportLost({ immediate: true })
  }

  async destroy () {
    if (this.closing) return this.closing
    this.destroyed = true
    this.closing = (async () => {
      if (this._reconnectTimer) clearTimeout(this._reconnectTimer)
      if (this._livenessTimer) clearInterval(this._livenessTimer)
      if (this.swarm) {
        await Promise.race([
          this.swarm.destroy().catch(() => {}),
          new Promise((resolve) => setTimeout(resolve, TEARDOWN_TIMEOUT))
        ])
        this.swarm = null
      }
      if (this.store) await this.store.close().catch(() => {})
      this._setStatus('offline')
    })()
    return this.closing
  }

  /* ---- swarm ---- */

  join (topic, { server = true, client = true } = {}) {
    const hex = b4a.toString(topic, 'hex')
    let entry = this._topics.get(hex)
    if (!entry) {
      entry = { topic, server, client, discovery: null, refs: 0 }
      this._topics.set(hex, entry)
    }
    entry.refs++
    entry.server = entry.server || server
    entry.client = entry.client || client
    if (this.swarm && !this.destroyed) {
      try {
        entry.discovery = this.swarm.join(entry.topic, { server: entry.server, client: entry.client })
      } catch {
        // The swarm went away between the check and the call. The topic stays
        // in the table and is rejoined by the next _connect().
      }
    }
    return entry.discovery
  }

  async leave (topic) {
    const hex = b4a.toString(topic, 'hex')
    const entry = this._topics.get(hex)
    if (!entry) return
    if (--entry.refs > 0) return
    this._topics.delete(hex)
    if (this.swarm) await this.swarm.leave(topic).catch(() => {})
  }

  async flush () {
    if (!this.swarm) return
    await this.swarm.flush()
  }

  /**
   * Wait until a topic is actually announced on the DHT.
   *
   * swarm.join() returns immediately, but the announce takes a round trip. A
   * peer that looks the topic up before that finishes finds nothing and then
   * sits on Hyperswarm's slow retry cycle, which is what makes "nobody is
   * online" appear for a room whose host is right there.
   */
  async announced (topic) {
    await this.online({ timeout: 15000 })
    const entry = this._topics.get(b4a.toString(topic, 'hex'))
    if (!entry || !entry.discovery) return false
    try {
      await entry.discovery.flushed()
      return true
    } catch {
      return false
    }
  }

  /** Force a fresh lookup for a topic we are a client of. */
  refresh (topic) {
    const entry = this._topics.get(b4a.toString(topic, 'hex'))
    if (!entry || !entry.discovery) return
    // refresh() is async, so a try/catch around the call catches nothing: a
    // lookup started against a discovery that is being destroyed rejected into
    // no handler at all and surfaced as an unhandled rejection during shutdown.
    try {
      Promise.resolve(entry.discovery.refresh()).catch(() => {})
    } catch { /* already destroyed */ }
  }

  /**
   * Announce every topic again, now.
   *
   * A record on the DHT lives twenty minutes, and Hyperswarm renews it from a
   * ten-minute timer. In a phone's WebView that timer counts only the time
   * the CPU is awake, and a phone asleep in a pocket can go hours without
   * reaching it. The records then run out while the link to the relay itself
   * stays up: the phone is connected and nobody can find it. Calls do not
   * arrive, nor messages, and people invited to its groups see nobody
   * online. The moments that do wake a phone (a push, the KeepAlive alarm)
   * call this, so the records are renewed by the wall clock instead of the
   * one that stops while the phone sleeps.
   *
   * Returns whether it ran: not while offline (the reconnect announces
   * everything anyway) and not twice within REANNOUNCE_GAP.
   */
  reannounce () {
    if (this.destroyed || this.closing || !this.swarm || this.status !== 'online') return false
    const now = Date.now()
    if (now - this._reannouncedAt < REANNOUNCE_GAP) return false
    this._reannouncedAt = now
    for (const entry of this._topics.values()) {
      if (!entry.discovery) continue
      // See refresh() for why the catch is on the promise.
      try {
        Promise.resolve(entry.discovery.refresh()).catch(() => {})
      } catch { /* already destroyed */ }
    }
    return true
  }

  /** Resolve once the swarm is up, or after `timeout` if it never comes. */
  online ({ timeout = 0 } = {}) {
    if (this.status === 'online') return Promise.resolve(true)
    return new Promise((resolve) => {
      let done = false
      const finish = (value) => {
        if (done) return
        done = true
        off()
        if (timer) clearTimeout(timer)
        resolve(value)
      }
      const timer = timeout ? setTimeout(() => finish(false), timeout) : null
      const off = this.onStatus((status) => {
        if (status === 'online') finish(true)
      })
    })
  }

  onConnection (fn) {
    this._connectionHandlers.add(fn)
    return () => this._connectionHandlers.delete(fn)
  }

  get connections () {
    return this.swarm ? [...this.swarm.connections] : []
  }

  get peerCount () {
    return this.swarm ? this.swarm.connections.size : 0
  }

  /* ---- status ---- */

  onStatus (fn) {
    this._listeners.add(fn)
    fn(this.status, this.mode)
    return () => this._listeners.delete(fn)
  }

  _setStatus (status) {
    if (this.status === status) return
    this.status = status
    for (const fn of this._listeners) fn(status, this.mode)
  }
}

export function corestoreNamespace (store, name) {
  return store.namespace(name)
}
